import { MassActionsContextProvider } from '@components/MassActions/contexts/MassActions.context';
import ListScreen from '@components/v2/layout/ListScreen/ListScreen';
import Breadcrumb from '@components/v2/ui/Breadcrumb/Breadcrumb';
import Typography from '@components/v2/ui/Typography/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useEffect } from 'react';
import { ParamValues } from '../../../../components/Crud/useFilter.types';
import { Column } from '../../../../components/ListV2/components/table/VirtualizedTable.types';
import { useInfiniteListWithCheckbox } from '../../../../hooks/useInfiniteListWithCheckbox/useInfiniteListWithCheckbox';
import { UseInfiniteListWithCheckboxTransformers } from '../../../../hooks/useInfiniteListWithCheckbox/utils/useInfiniteListWithCheckbox.transformers';
import useSearchParams from '../../../../hooks/useSearchParams';
import { useTabSearchParam } from '../../../../hooks/useTabSearchParam';
import LangEnum from '../../../../lang/LangEnum';
import { RoutePageProps } from '../../../../routes/routes.types';
import { useAppEnvironmentStore } from '../../../../stores/AppEnvironment/appEnvironment.store';
import { selectIsInStoreEnvironment } from '../../../../stores/AppEnvironment/appEnvironment.store.selectors';
import OfflineAlert from '../../../offline/components/OfflineAlert/OfflineAlert';
import useOfflineOrdersSyncingHandler from '../../../offline/hooks/useOfflineOrdersSyncingHandler/useOfflineOrdersSyncingHandler';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import useOrderBook from '../../../order-book/hooks/useOrderBook';
import { useTranslation } from '../../../translation/TranslationContext';
import { useAbility } from '../../../users/AbilityContext';
import {
  OrderActionsContextProvider,
  useOrderActionsContext,
} from '../../contexts/OrderActions.context';
import useOrderStatusOptions from '../../hooks/useOrderStatusOptions';
import { getOrderTotalsQueryKey } from '../../hooks/useOrderTotals/order-totals.keys';
import OrderList from '../../list/OrderList';
import OrderTableTabs from '../../list/OrderTableTabs';
import { ORDER_DEFAULT_ORDER } from '../../list/orderList.columns';
import { OrderListTabName, OrderTab, getOrderStatusByTab } from '../../list/orderListTabs';
import OrderModals from '../../modals/OrderModals';
import { OrderListWithBaseItem } from '../../order.types';
import { UseOrderFilterV2Result } from '../../orderFilter/hooks/useOrderFilterV2';
import { formatOrderFilters } from '../../orderFilter/orderFilter.utils';
import OrderListMassActionModals from './components/OrderListMassActionModals';
import OrderListPageFilters from './components/OrderListPageFilters';
import { useOrderListColumns } from './hooks/useOrderListColumns';
import { useOrderListPageMassActions } from './hooks/useOrderListPageMassActions';
import { OrderListPageList } from './orderListPage.types';
import {
  ACCEPTED_TABS_TO_FETCH_OFFLINE,
  KEY_TO_DETECT_TAB_TO_OFFLINE,
  offlineOrderListFnToInfiniteListWithCheckbox,
  orderListFnToInfiniteListWithCheckbox,
} from './utils/orderListFetcherWithOfflineOrders.utils';
import { isArchivedOrderFilterActive, toListScreenFilters } from './utils/orderListPage.utils';

type OrderListPageProps = RoutePageProps &
  UsedInOfflineMode & {
    list: OrderListPageList;
    fixedCustomCount: number;
    columns: Column<OrderListWithBaseItem>[];
    isLoadingTotals: boolean;
    selectedTab: OrderListTabName;
    onChangeTab: (tab: OrderTab) => void;
    reloadList: (params?: ParamValues) => void;
    restoreScrollOnMount: boolean;
    filters?: UseOrderFilterV2Result;
  };

const OrderListPage = ({
  list,
  columns,
  isLoadingTotals,
  selectedTab,
  onChangeTab,
  fixedCustomCount,
  reloadList,
  restoreScrollOnMount,
  filters,
  isOfflineMode = false,
}: OrderListPageProps) => {
  const { searchParams } = useSearchParams();

  const isArchivedFilterActive = !isOfflineMode && isArchivedOrderFilterActive(searchParams);
  const isInStoreEnvironment = useAppEnvironmentStore(selectIsInStoreEnvironment);

  const ability = useAbility();
  const { translate } = useTranslation();
  const { openOrderBook } = useOrderBook();

  const { massActionsState, isAllItemsSelected } = useOrderListPageMassActions({
    list,
    selectedTab,
    isOfflineMode,
    isArchivedOrderFilterActive: isArchivedFilterActive,
    totalResults: fixedCustomCount,
  });

  const { setReload } = useOrderActionsContext();
  useEffect(() => {
    setReload(reloadList);
  }, [setReload, reloadList]);

  const orderTableTabs = <OrderTableTabs onChangeTab={onChangeTab} selectedTab={selectedTab} />;

  const offlineAlert = (
    <OfflineAlert>
      <Typography variant="body2">{translate(LangEnum.OFFLINE_ALERT_TO_LIST)}</Typography>
    </OfflineAlert>
  );

  const pageTitle = isOfflineMode ? LangEnum.GLOBAL_OFFLINE_ORDERS : LangEnum.GLOBAL_ORDERS;

  const primaryAction = {
    label: LangEnum.GLOBAL_CREATE_ORDER,
    icon: Plus,
    variant: 'text' as const,
    onClick: openOrderBook,
    isHidden: ability.cannot('create', 'orders') || isInStoreEnvironment,
  };

  const handleToggleSelectParent = (
    _parentItem: OrderListWithBaseItem,
    childItems: OrderListWithBaseItem[],
  ) => {
    list.checkbox.toggleSelectParent(childItems.map((c) => c.orderId!));
  };

  const orderListProps = {
    columns,
    isOfflineMode,
    isAllItemsSelected,
    orders: list.state.items,
    ordersSelected: list.state.selecteds,
    isLoadingPage: list.state.isLoadingPage,
    isLoadingTotals,
    loadMore: list.loadMoreRows,
    onToggleSelectAll: list.checkbox.toggleSelectAllWithParents,
    onToggleSelect: list.checkbox.toggleSelect,
    onToggleSelectParent: handleToggleSelectParent,
    isLoadingMore: list.state.isLoadingMore,
    isArchivedOrderFilterActive: isArchivedFilterActive,
    restoreScrollOnMount,
    isCreateOrderHidden: primaryAction.isHidden,
    onCreateOrder: primaryAction.onClick,
  };

  const listScreenToolbar = isOfflineMode
    ? { toolbarStart: offlineAlert }
    : { tabs: orderTableTabs };

  return (
    <>
      <OrderListMassActionModals
        isOfflineMode={isOfflineMode}
        massActionsState={massActionsState}
        reloadList={reloadList}
      />
      <ListScreen
        toolbar={listScreenToolbar}
        filters={toListScreenFilters(filters)}
        header={{
          breadcrumb: <Breadcrumb items={[{ label: pageTitle }]} />,
          primaryAction,
        }}
      >
        {(scrollParent) => <OrderList {...orderListProps} customScrollParent={scrollParent} />}
      </ListScreen>
    </>
  );
};

type OrderListPageContainerProps = RoutePageProps & UsedInOfflineMode;

const OrderListPageContainer = ({
  isOfflineMode = false,
  ...props
}: OrderListPageContainerProps) => {
  const queryClient = useQueryClient();

  const { searchParams } = useSearchParams(undefined, { searchParamsToIgnore: ['tab'] });

  const orderStatusOptions = useOrderStatusOptions();

  const [selectedTabName, setSelectedTabName] = useTabSearchParam<OrderListTabName>(
    'tab',
    OrderListTabName.ALL,
  );
  const selectedTab = isOfflineMode ? OrderListTabName.ALL : selectedTabName;
  const orderStatus = getOrderStatusByTab(selectedTab);

  const onlineQueryFnParams = {
    [KEY_TO_DETECT_TAB_TO_OFFLINE]: selectedTab,
    status: orderStatusOptions
      .filter((statusOption) => orderStatus?.includes(statusOption.value))
      .map((statusOption) => statusOption.value as string),
    ...formatOrderFilters(searchParams),
  };

  const infiniteListWithCheckbox = useInfiniteListWithCheckbox<OrderListWithBaseItem>({
    limit: 20,
    cacheKey: isOfflineMode ? 'offline-orders-list' : 'orders-list',
    defaultOrderBy: ORDER_DEFAULT_ORDER,
    queryFn: isOfflineMode
      ? offlineOrderListFnToInfiniteListWithCheckbox
      : orderListFnToInfiniteListWithCheckbox,
    queryFnParams: isOfflineMode ? undefined : onlineQueryFnParams,
  });

  const { columns, isLoadingTotals } = useOrderListColumns({ selectedTab, isOfflineMode });

  const infinityListManager =
    UseInfiniteListWithCheckboxTransformers.toInfinityListManager(infiniteListWithCheckbox);

  const massActionData =
    UseInfiniteListWithCheckboxTransformers.toMassActionData(infiniteListWithCheckbox);

  const reloadOnAnyOfflineOrderChange = () => {
    infinityListManager.reload();
    const onlyPathToInvalidateAll = getOrderTotalsQueryKey({})[0];
    queryClient.invalidateQueries({
      queryKey: [onlyPathToInvalidateAll],
    });
  };

  const { handleWithOfflineOrdersSyncing } = useOfflineOrdersSyncingHandler({
    onAnyChange: reloadOnAnyOfflineOrderChange,
  });

  useEffect(() => {
    if (ACCEPTED_TABS_TO_FETCH_OFFLINE.includes(selectedTab)) {
      handleWithOfflineOrdersSyncing();
    }
  }, [selectedTab]);

  const renderPage = (filters?: UseOrderFilterV2Result) => (
    <OrderListPage
      {...props}
      isOfflineMode={isOfflineMode}
      list={infinityListManager}
      columns={columns}
      isLoadingTotals={isLoadingTotals}
      filters={filters}
      fixedCustomCount={infiniteListWithCheckbox.query.countData?.virtualTotal || 0}
      onChangeTab={(tab) => {
        setSelectedTabName(tab.name);
        infinityListManager.checkbox.toggleSelectBatch([]);
      }}
      selectedTab={selectedTab}
      reloadList={infinityListManager.reload}
      restoreScrollOnMount={infiniteListWithCheckbox.restoreScrollOnMount}
    />
  );

  return (
    <OrderActionsContextProvider isOfflineMode={isOfflineMode}>
      <OrderModals
        isOfflineMode={isOfflineMode}
        onSuccessSyncOfflineOrder={reloadOnAnyOfflineOrderChange}
      />
      <MassActionsContextProvider data={massActionData}>
        {isOfflineMode ? (
          renderPage()
        ) : (
          <OrderListPageFilters
            filtersConfig={{
              columns,
              selectedTab,
              selectedIds: infinityListManager.state.selecteds,
            }}
          >
            {renderPage}
          </OrderListPageFilters>
        )}
      </MassActionsContextProvider>
    </OrderActionsContextProvider>
  );
};

export default OrderListPageContainer;
